/**
 * Wire-compatible snap-integration.v1 browser projection types (PR-8.6B / PR-8.6C).
 * Mirrors SnapBrowserBootstrapProjection — browser-safe fields only.
 */

export const SNAP_CONTRACT_VERSION = 'snap-integration.v1' as const;
export const SNAP_CONTRACT_FAMILY = 'snap-integration';
export const SNAP_PACKAGE_ID = '@noevault/snap';
/** Controlled-pilot package version — bump on every publishable SDK correction. */
export const SNAP_PACKAGE_VERSION = '1.1.3';
/** Safe build marker for hosted stale-asset proof (no secrets). */
export const SNAP_BUILD_MARKER = 'PR-8.7E.4A';

export const SnapDenyCodes = {
  IntegrationDisabled: 'SNAP_INTEGRATION_DISABLED',
  Unauthorized: 'SNAP_UNAUTHORIZED',
  SolutionInactive: 'SNAP_SOLUTION_INACTIVE',
  OrganisationMismatch: 'SNAP_ORGANISATION_MISMATCH',
  SolutionMismatch: 'SNAP_SOLUTION_MISMATCH',
  EntitlementUnavailable: 'SNAP_ENTITLEMENT_UNAVAILABLE',
  GrainMissing: 'SNAP_GRAIN_MISSING',
  BootstrapInvalid: 'SNAP_BOOTSTRAP_INVALID',
  BootstrapExpired: 'SNAP_BOOTSTRAP_EXPIRED',
  BootstrapAudience: 'SNAP_BOOTSTRAP_AUDIENCE',
  BootstrapTampered: 'SNAP_BOOTSTRAP_TAMPERED',
  ConfigurationInvalid: 'SNAP_CONFIGURATION_INVALID',
  BindingUnavailable: 'SNAP_BINDING_UNAVAILABLE',
  ContractMismatch: 'SNAP_CONTRACT_MISMATCH',
  CaptureDisabled: 'SNAP_CAPTURE_DISABLED',
  CaptureFailed: 'SNAP_CAPTURE_FAILED',
  ContextInvalid: 'SNAP_CONTEXT_INVALID',
  AttachmentRejected: 'SNAP_ATTACHMENT_REJECTED',
  SubmissionInProgress: 'SNAP_SUBMISSION_IN_PROGRESS',
  TransportFailed: 'SNAP_TRANSPORT_FAILED',
  Cancelled: 'SNAP_CANCELLED',
  SoftDisabled: 'SNAP_SOFT_DISABLED',
  AppKeyRejected: 'SNAP_APPKEY_REJECTED',
  // PR-8.7D.5 — account claim / Open in NoeVault.
  ClaimDisabled: 'REQUESTER_CLAIM_DISABLED',
  ClaimAlreadyLinked: 'REQUESTER_ALREADY_LINKED'
} as const;

export type SnapDenyCode = (typeof SnapDenyCodes)[keyof typeof SnapDenyCodes];

export interface SnapCapabilityStatus {
  capabilityKey?: string;
  commercialKey?: string;
  available: boolean;
  state: string;
  softDisabled: boolean;
  createAllowed: boolean;
  myRequestsAllowed?: boolean;
  captureAllowed?: boolean;
  evidenceAllowed?: boolean;
}

export interface SnapTenantSolutionContext {
  organisationUid: string;
  solutionUid: string;
  userUid?: string | null;
  environmentUid?: string | null;
  componentUid?: string | null;
  runtimeRegistrationUid?: string | null;
  organisationDisplayName?: string | null;
  solutionDisplayName?: string | null;
}

export interface SnapUiConfiguration {
  launcherEnabled: boolean;
  defaultPlacement: string;
  assistEnabled: boolean;
  consentMessage?: string | null;
  productName?: string;
  reportLabel?: string;
  myRequestsLabel?: string;
}

export interface SnapCapturePolicy {
  requiresUserInitiation: boolean;
  screenshotEnabled: boolean;
  domMetadataEnabled: boolean;
  clientErrorCaptureEnabled: boolean;
  includePageUrl: boolean;
  includeQueryString: boolean;
  includePageTitle: boolean;
}

export interface SnapPrivacyRedactionPolicy {
  neverCapturePasswords: boolean;
  neverCaptureTokensOrSecrets: boolean;
  maskInputTypes: string[];
  maskAttributeSelectors: string[];
  maskCssSelectors: string[];
}

export interface SnapEvidenceConstraints {
  maxFileBytes: number;
  allowedMimeTypes: string[];
  allowedExtensions: string[];
  retentionPolicyRef?: string;
  malwareScanRequired?: boolean;
  maxAttachmentCount?: number;
}

export interface SnapEndpointDiscovery {
  contractVersion: string;
  bootstrapPath: string;
  authorizePath: string;
  ticketCreatePath: string;
  myRequestsPath?: string;
  requestDetailPathTemplate?: string;
  commentPath?: string;
  attachmentPathTemplate: string;
  correlationHeaderName: string;
  bootstrapHeaderName: string;
}

export interface SnapPriorityOption {
  value: number;
  label: string;
}

export interface SnapCategoryOption {
  id: number;
  name: string;
}

/** PR-8.7C — experience metadata from bootstrap (non-authoritative display + capability hints). */
export interface SnapExperienceConfiguration {
  categorySupport: boolean;
  prioritySupport: boolean;
  defaultPriority: number;
  priorityOptions: SnapPriorityOption[];
  categories: SnapCategoryOption[];
  maxAttachmentCount: number;
}

/**
 * PR-8.7D.5 — optional account-claim offer from bootstrap.
 *
 * Display metadata only. It never carries a claim token or verification code, and it never gates any
 * embedded capability: Report, My Requests, comment, and evidence all work while `offered` is false.
 */
export interface SnapClaimOffer {
  /** True when this requester may start a claim now. */
  offered: boolean;
  /** True once this requester identity is already linked to a NoeVault account. */
  alreadyClaimed: boolean;
  /** True when no address is on file, so the claim form must ask for one. */
  requiresEmail: boolean;
  /** Masked recipient for confirmation copy. Never the full address. */
  maskedEmail?: string | null;
  invitePath: string;
  completePath: string;
  /** Portal link with `{claimToken}` left unresolved — substituted by the claim email, not the browser. */
  deepLinkTemplate?: string | null;
  /** Requester-only capability set a completed claim confers. Never admin or ops rights. */
  portalCapabilities: string[];
  offerLabel: string;
  openInNoeVaultLabel: string;
  offerDescription: string;
}

/** PR-8.7D.5 — start a claim for the current bootstrap session. */
export interface SnapClaimInviteRequest {
  /** Accepted only when the requester has no address on file. */
  email?: string;
  correlationId?: string;
}

export interface SnapClaimInviteResult {
  success: boolean;
  correlationId: string;
  code?: string | null;
  message: string;
  maskedEmail?: string | null;
  expiresAtUtc?: string | null;
}

/** PR-8.7D.5 — complete a claim. The token alone is never sufficient. */
export interface SnapClaimCompleteRequest {
  claimToken: string;
  /** One-time code from the claim email. */
  verificationCode: string;
  /** Optional confirmation address; must match the address the proof was issued to. */
  email?: string;
  correlationId?: string;
}

export interface SnapClaimCompleteResult {
  success: boolean;
  correlationId: string;
  code?: string | null;
  message: string;
  userUid?: string | null;
  linkedExistingUser: boolean;
  requiresPasswordSetup: boolean;
  portalCapabilities: string[];
}

export interface SnapOptionalContextAcceptance {
  field: string;
  value?: string | null;
  accepted: boolean;
  reason: string;
}

/** Browser-safe bootstrap projection from NoeVault.Snap.Client. */
export interface SnapBrowserBootstrap {
  contractVersion: string;
  correlationId: string;
  authorization?: string | null;
  expiresAtUtc: string;
  expiresInSeconds: number;
  capability: SnapCapabilityStatus;
  context: SnapTenantSolutionContext;
  ui?: SnapUiConfiguration | null;
  capturePolicy?: SnapCapturePolicy | null;
  privacyPolicy?: SnapPrivacyRedactionPolicy | null;
  evidenceConstraints?: SnapEvidenceConstraints | null;
  endpoints?: SnapEndpointDiscovery | null;
  experience?: SnapExperienceConfiguration | null;
  /** PR-8.7D.5 — optional claim offer. Absent or `offered: false` simply hides the affordance. */
  claim?: SnapClaimOffer | null;
  grantedGrains?: string[];
  optionalContext?: SnapOptionalContextAcceptance[];
  sdkPackageId?: string;
  sdkPackageVersion?: string;
}

export type SnapFailureKind =
  | 'bootstrap_invalid'
  | 'bootstrap_expired'
  | 'authorization_missing'
  | 'contract_mismatch'
  | 'appkey_rejected'
  | 'soft_disabled'
  | 'capability_denied'
  | 'capture_disabled'
  | 'capture_failed'
  | 'context_invalid'
  | 'attachment_rejected'
  | 'submission_denied'
  | 'transport_failed'
  | 'cancelled'
  | 'configuration_invalid'
  | 'destroyed';

export interface SnapError {
  kind: SnapFailureKind;
  code: string;
  message: string;
  correlationId?: string;
  retryable: boolean;
  userSafe: boolean;
}

export type SnapEventName =
  | 'initialized'
  | 'ready'
  | 'opened'
  | 'closed'
  | 'captureStarted'
  | 'captureCompleted'
  | 'captureFailed'
  | 'submissionStarted'
  | 'submissionSucceeded'
  | 'requestCreated'
  | 'submissionDenied'
  | 'submissionFailed'
  | 'bootstrapExpired'
  | 'sessionExpired'
  | 'unauthorized'
  | 'error'
  | 'destroyed';

export type SnapEventMap = {
  initialized: { correlationId: string };
  ready: { correlationId: string };
  opened: Record<string, never>;
  closed: { reason?: string };
  captureStarted: Record<string, never>;
  captureCompleted: { width: number; height: number; redacted: boolean };
  captureFailed: { error: SnapError };
  submissionStarted: { correlationId: string };
  submissionSucceeded: {
    ticketId?: number | string;
    ticketNumber?: string;
    correlationId: string;
  };
  requestCreated: {
    ticketId?: number | string;
    ticketNumber?: string;
    correlationId: string;
  };
  submissionDenied: { error: SnapError };
  submissionFailed: { error: SnapError };
  bootstrapExpired: { error: SnapError };
  sessionExpired: { error: SnapError };
  unauthorized: { error: SnapError };
  error: { error: SnapError };
  destroyed: Record<string, never>;
};

export type SnapEventHandler<K extends SnapEventName> = (
  payload: SnapEventMap[K]
) => void;

export type SnapLauncherPlacement =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';

export type SnapDensity = 'comfortable' | 'compact';
export type SnapColorMode = 'light' | 'dark' | 'system';

export interface SnapThemeOptions {
  accentColor?: string;
  foreground?: string;
  background?: string;
  borderRadius?: string;
  launcherPosition?: SnapLauncherPlacement;
  density?: SnapDensity;
  colorMode?: SnapColorMode;
  zIndex?: number;
}

export interface SnapApplicationContext {
  [key: string]: unknown;
}

export interface SnapCreateOptions {
  /** Browser-safe bootstrap projection (required for initialize). */
  bootstrap?: SnapBrowserBootstrap;
  /** Absolute API base used to resolve relative endpoint paths. */
  apiBaseUrl?: string;
  /** Optional host callback to obtain refreshed bootstrap. */
  refreshBootstrap?: () => Promise<SnapBrowserBootstrap>;
  /** Mount host element; defaults to document.body. */
  mount?: HTMLElement | string;
  /** Whether to render the floating launcher. Default true when UI allows. */
  showLauncher?: boolean;
  theme?: SnapThemeOptions;
  /** Application-supplied safe context hints (non-authoritative). */
  context?: SnapApplicationContext;
  /** Custom fetch implementation (tests / harness). */
  fetch?: typeof fetch;
  /** Optional clock for expiry checks (tests). */
  now?: () => Date;
  /** AbortSignal for initialize/submit coordination. */
  signal?: AbortSignal;
}

export interface SnapSubmitInput {
  title: string;
  description: string;
  priority?: number;
  categoryId?: number | null;
  attachments?: File[];
  includeScreenshot?: boolean;
  screenshotDescription?: string;
  signal?: AbortSignal;
}

export interface SnapSubmitResult {
  success: boolean;
  ticketId?: number | string;
  ticketNumber?: string;
  correlationId: string;
  error?: SnapError;
}

export interface NoeVaultSnapInstance {
  initialize(bootstrap?: SnapBrowserBootstrap): Promise<void>;
  /** Alias for initialize — controlled-pilot public surface. */
  init(bootstrap?: SnapBrowserBootstrap): Promise<void>;
  open(): void;
  /** Open the Report an issue form (canonical Snap Support report mode). */
  openReport(): void;
  /** Open My Requests list for the trusted external subject. */
  openMyRequests(): void | Promise<void>;
  close(reason?: string): void;
  refreshBootstrap(bootstrap?: SnapBrowserBootstrap): Promise<void>;
  /** Alias for refreshBootstrap. */
  refreshContext(bootstrap?: SnapBrowserBootstrap): Promise<void>;
  setContext(context: SnapApplicationContext): void;
  submit(input: SnapSubmitInput): Promise<SnapSubmitResult>;
  /**
   * PR-8.7D.5 — the optional account-claim offer from bootstrap, or null when nothing is offered.
   * Embedded Snap stays fully usable either way; use this only to decide whether to render the CTA.
   */
  getClaimOffer(): SnapClaimOffer | null;
  /**
   * PR-8.7D.5 — start an account claim ("Claim your support history" / "Open in NoeVault").
   * The claim link and one-time code are emailed to the requester; nothing sensitive is returned.
   */
  requestAccountClaim(request?: SnapClaimInviteRequest): Promise<SnapClaimInviteResult>;
  destroy(): void;
  on<K extends SnapEventName>(event: K, handler: SnapEventHandler<K>): void;
  off<K extends SnapEventName>(event: K, handler: SnapEventHandler<K>): void;
  getCorrelationId(): string | null;
  isOpen(): boolean;
  isDestroyed(): boolean;
}
